import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { AuthenticationDataService } from 'app/core/authentication/services/authentication-data.service';
import { UrlService } from 'app/core/http/url.service';
import { UserAvatar } from '../models/user-avatar.model';

@Injectable({
  providedIn: 'root'
})
export class AvatarService {
  private static readonly AVATAR_ENDPOINT = 'users/user-bot-parts/get-avatar';

  private readonly httpClient = inject(HttpClient);
  private readonly urlService = inject(UrlService);
  private readonly authenticationDataService = inject(AuthenticationDataService);

  private _myCachedAvatar: UserAvatar | null = null;
  private _cachedForUserId: string | null = null;
  private _inflight$: Observable<UserAvatar> | null = null;

  public bustMyAvatarCache(): void {
    this._myCachedAvatar = null;
    this._cachedForUserId = null;
    this._inflight$ = null;
  }

  public getMyAvatar(): Observable<UserAvatar> {
    const currentUserId = this.authenticationDataService.getUserId();
    if (!currentUserId) {
      return throwError(() => new Error('User ID is not set when fetching Avatar'));
    }

    if (this._cachedForUserId !== currentUserId) {
      this.bustMyAvatarCache();
    }
    else if (this._myCachedAvatar) {
      return of(this._myCachedAvatar);
    }

    if (!this._inflight$) {
      const url = this.urlService.buildGameServerApiUrl(AvatarService.AVATAR_ENDPOINT);
      this._inflight$ = this.httpClient.post<UserAvatar[]>(url, { userIds: [currentUserId] })
        .pipe(
          map((avatars) => {
            if (!avatars?.length) {
              throw new Error('No avatar found for the current user');
            }
            return avatars[0];
          }),
          tap((avatar) => {
            if (avatar?.avatarURL) {
              this._myCachedAvatar = avatar;
              this._cachedForUserId = currentUserId;
            }
            else {
              this._myCachedAvatar = null;
              this._cachedForUserId = null;
            }
            this._inflight$ = null;
          }),
          shareReplay(1)
        );
    }

    return this._inflight$;
  }

  public getUserAvatar(userId: string): Observable<UserAvatar> {
    const localUserId = this.authenticationDataService.getUserId();
    if (localUserId === userId) {
      return this.getMyAvatar();
    }
    const url = this.urlService.buildGameServerApiUrl(AvatarService.AVATAR_ENDPOINT);
    return this.httpClient.post<UserAvatar[]>(url, { userIds: [userId] }).pipe(
      map((avatars) => avatars[0])
    );
  }

  public getUsersAvatars(userIds: string[]): Observable<UserAvatar[]> {
    const url = this.urlService.buildGameServerApiUrl(AvatarService.AVATAR_ENDPOINT);
    return this.httpClient.post<UserAvatar[]>(url, { userIds });
  }
}
